/**
 * PDF Payload Configuration System
 * 
 * This module provides a reusable, dynamic configuration system for PDF payload generation.
 * Similar to how the Blazor version dynamically loads report classes, this allows
 * PDF-specific payload transformations to be configured per PDF type.
 * 
 * Usage:
 * 1. Add a configuration entry for your PDF type in pdfPayloadConfigs
 * 2. Define field mappings, whitelist, and default values
 * 3. The system will automatically apply transformations when generating PDF payloads
 */

export type PDFPayloadConfig = {
  /**
   * Field mappings: Maps source fields to target fields
   * - Single field: "targetField": "sourceField"
   * - Multiple sources (fallback): "targetField": ["sourceField1", "sourceField2"]
   */
  fieldMappings?: Record<string, string | string[]>;
  
  /**
   * Field whitelist: Only these fields will be included in the payload
   * If not specified, all fields from formData will be included
   */
  fieldWhitelist?: string[];
  
  /**
   * Default values: Default values for fields when they're missing
   */
  defaultValues?: Record<string, any>;
  
  /**
   * Custom payload builder: Optional custom function to build payload
   * If provided, this will override fieldMappings and fieldWhitelist
   */
  customBuilder?: (formData: any, logined_userid: string) => any;
};

/**
 * PDF Payload Configurations
 * 
 * Add new PDF types here by adding entries to this object.
 * The key should match the PDF name (Settings value from button config).
 */
export const pdfPayloadConfigs: Record<string, PDFPayloadConfig> = {
  charts_of_accounts_report: {
    customBuilder: (formData: any, logined_userid: string) => {
      // In some modules `ledgerid` comes as a composite string like:
      // "211e5f00-..._000001_260212174649352_1"
      // The actual ledger id is the last segment ("1"). Normalize it.
      const rawLedger =
        formData?.ledgerid ??
        formData?.ledger_id ??
        formData?.id ??
        formData?.Id ??
        "";

      const rawLedgerStr = rawLedger != null ? String(rawLedger) : "";
      const normalizedLedgerId =
        rawLedgerStr.includes("_")
          ? rawLedgerStr.split("_").pop() || rawLedgerStr
          : rawLedgerStr;

      return {
        ...formData,
        id: normalizedLedgerId,
        ledgerid: normalizedLedgerId,
        ledger_id: normalizedLedgerId,
        logined_userid,
      };
    },
  },
  Estimation: {
    fieldMappings: {
      parent_id: ["inspection_id", "parent_id"], // Map inspection_id or parent_id to parent_id
      parent_id_desc: ["vin_no", "parent_id_desc"], // Map vin_no or parent_id_desc to parent_id_desc
      est_total_amount: ["est_total_amount", "txt_total_amount"], // Map txt_total_amount to est_total_amount
    },
    fieldWhitelist: [
      "id",
      "est_no",
      "est_date",
      "parent_id",
      "parent_id_desc",
      "remarks",
      "est_days",
      "customer_remarks",
      "spare_amount",
      "spare_vat_amount",
      "spare_net_amount",
      "labour_amount",
      "labour_vat_amount",
      "labour_net_amount",
      "miscellaneous_amount",
      "miscellanious_vat_amount",
      "miscellaneous_net_amount",
      "gross_vat_amount",
      "gross_amount",
      "est_total_amount",
      "locked",
      "current_state",
    ],
    defaultValues: {
      remarks: null,
      est_days: null,
      customer_remarks: null,
      spare_amount: 0,
      spare_vat_amount: 0,
      spare_net_amount: 0,
      labour_amount: 0,
      labour_vat_amount: 0,
      labour_net_amount: 0,
      miscellaneous_amount: 0,
      miscellanious_vat_amount: 0,
      miscellaneous_net_amount: 0,
      gross_vat_amount: 0,
      gross_amount: 0,
      est_total_amount: 0,
      locked: 0,
      current_state: "",
    },
  },
  inspection: {
    fieldMappings: {
      // Map common field variations if needed
      vin_no: ["vin_no", "vin_number"],
      vehicle_id: ["vehicle_id", "drp_veh_reg_no"],
    },
    fieldWhitelist: [
      "id",
      "vin_no",
      "vin_date",
      "vehicle_id",
      "vehicle_make",
      "vehicle_type",
      "vehicle_model",
      "vehicle_eng_no",
      "vehicle_chas_no",
      "km",
      "job_category_id",
      "salesman_id",
      "service_advisor_id",
      "fuel_level",
      "driver_name",
      "drive_mobile",
      "remarks",
      "del_time",
      "current_state",
      "locked",
    ],
    defaultValues: {
      vin_no: "",
      vin_date: null,
      vehicle_id: null,
      vehicle_make: null,
      vehicle_type: null,
      vehicle_model: null,
      vehicle_eng_no: null,
      vehicle_chas_no: null,
      km: 0,
      job_category_id: null,
      salesman_id: null,
      service_advisor_id: null,
      fuel_level: null,
      driver_name: null,
      drive_mobile: null,
      remarks: null,
      del_time: null,
      current_state: "",
      locked: 0,
    },
  },
  // Add more PDF configurations here as needed
  // Example:
  // JobCard: {
  //   fieldMappings: {
  //     vehicle_id: ["vehicle_id", "vin_no"],
  //   },
  //   fieldWhitelist: ["id", "job_no", "vehicle_id", ...],
  //   defaultValues: { ... },
  // },
};

/**
 * Builds PDF payload dynamically based on configuration
 * 
 * @param pdfName - The PDF name (from Settings in button config)
 * @param formData - The form data to transform
 * @param logined_userid - The logged-in user ID
 * @returns The transformed payload object
 */
export function buildPDFPayload(
  pdfName: string,
  formData: any,
  logined_userid: string
): any {
  const config = pdfPayloadConfigs[pdfName];
  
  // If custom builder is provided, use it
  if (config?.customBuilder) {
    return config.customBuilder(formData, logined_userid);
  }
  
  // Start with base payload
  let payload: any = {
    id: formData.id || formData.Id || "",
    logined_userid: logined_userid,
  };
  
  // If no config, return all form data (default behavior)
  if (!config) {
    return {
      ...formData,
      id: formData.id || formData.Id || "",
      logined_userid: logined_userid,
    };
  }
  
  // Apply field mappings
  const mappedFields: Record<string, any> = {};
  if (config.fieldMappings) {
    Object.entries(config.fieldMappings).forEach(([targetField, sourceFields]) => {
      if (Array.isArray(sourceFields)) {
        // Try each source field in order until we find a value
        let found = false;
        for (const sourceField of sourceFields) {
          if (formData[sourceField] !== undefined && formData[sourceField] !== null) {
            // Allow empty strings and 0 as valid values
            mappedFields[targetField] = formData[sourceField];
            found = true;
            break;
          }
        }
        // If no value found, use default from defaultValues
        if (!found) {
          mappedFields[targetField] = config.defaultValues?.[targetField] ?? null;
        }
      } else {
        // Single source field mapping
        const value = formData[sourceFields];
        mappedFields[targetField] =
          value !== undefined && value !== null
            ? value
            : config.defaultValues?.[targetField] ?? null;
      }
    });
  }
  
  // Build payload from whitelist
  if (config.fieldWhitelist) {
    config.fieldWhitelist.forEach((fieldName) => {
      // Use mapped value if exists, otherwise use formData value, otherwise use default
      if (mappedFields[fieldName] !== undefined) {
        payload[fieldName] = mappedFields[fieldName];
      } else if (formData[fieldName] !== undefined && formData[fieldName] !== null) {
        payload[fieldName] = formData[fieldName];
      } else {
        payload[fieldName] = config.defaultValues?.[fieldName] ?? null;
      }
    });
  } else {
    // If no whitelist, include all mapped fields and formData
    payload = {
      ...formData,
      ...mappedFields,
      id: formData.id || formData.Id || "",
      logined_userid: logined_userid,
    };
  }
  
  return payload;
}

/**
 * Registers a new PDF payload configuration
 * Useful for dynamically adding configurations at runtime
 * 
 * @param pdfName - The PDF name
 * @param config - The configuration object
 */
export function registerPDFPayloadConfig(
  pdfName: string,
  config: PDFPayloadConfig
): void {
  pdfPayloadConfigs[pdfName] = config;
}

/**
 * Gets a PDF payload configuration
 * 
 * @param pdfName - The PDF name
 * @returns The configuration object or undefined
 */
export function getPDFPayloadConfig(pdfName: string): PDFPayloadConfig | undefined {
  return pdfPayloadConfigs[pdfName];
}

